import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AccruedRevenueReport.scss';
import { TableUtility } from '@utility/dynamic-table';
import MilestoneInitial from '../MilestoneMIS/MilestoneInitial';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { ACCRUED_REVENUE_REPORT } from '../../../Api/resources/endPoint';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.RESELLER_SERVICE);
  const servicePort = appendServicePortToURL(serviceName.RESELLER_SERVICE);
  let baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (!baseUrl) {
    if (
      process.env.NODE_ENV === 'production'
      && typeof window !== 'undefined'
      && window.location
      && window.location.origin
    ) {
      baseUrl = window.location.origin.replace(/\/$/, '');
    } else {
      baseUrl = 'http://localhost';
    }
  }

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${ACCRUED_REVENUE_REPORT}`;
  }

  const hasPort = /:\d+$/.test(baseUrl);
  if (hasPort) {
    return `${baseUrl}${serviceURL}/api${ACCRUED_REVENUE_REPORT}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${ACCRUED_REVENUE_REPORT}`;
};

const baseURL = fetchBaseURL();

const NUMERIC_COLUMN_HEADER_PATTERNS = [
  /total revenue/i,
  /total accrued revenue/i,
  /overall accrued revenue/i,
  /poc %/i,
  /total consumed cost/i,
  /total re-estimated cost/i,
  /allocated effort/i,
  /current month effort/i,
  /actual efforts itd/i,
  /total effort estimated/i,
  /estimated deviation/i,
  /agm/i,
  /egm/i,
];

const hasFilterValue = (item) => {
  if (!item || item?.type !== 'filter') return false;
  if (Array.isArray(item?.selectedValues) && item.selectedValues.length > 0) {
    return true;
  }
  if (Array.isArray(item?.range)) return item.range.some(Boolean);
  return Boolean(item?.selectedValue ?? item?.inputValue ?? item?.value);
};

const getAppliedFiltersFromConfiguration = (tableConfiguration = []) =>
  tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .filter(hasFilterValue);

const NUMERIC_CELL_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatNumericCellValue = (value) => {
  if (value === undefined || value === null || value === '') return value;
  const numericValue = typeof value === 'number'
    ? value
    : Number(
      String(value)
        .replace(/,/g, '')
        .trim(),
    );
  if (!Number.isFinite(numericValue)) return value;
  return NUMERIC_CELL_FORMATTER.format(numericValue);
};

const applyNumericFormatting = (tableConfiguration, tableData) => {
  const numericFields = (tableConfiguration || [])
    .filter((col) => {
      const headerText = (col.header || '').toLowerCase();
      return NUMERIC_COLUMN_HEADER_PATTERNS.some((pattern) => pattern.test(headerText));
    })
    .map((col) => col.field);

  if (!numericFields.length) return tableData;

  const formatRow = (row) => {
    if (!row || typeof row !== 'object') return row;
    const updatedRow = { ...row };
    numericFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(updatedRow, field)) {
        updatedRow[field] = formatNumericCellValue(updatedRow[field]);
      }
    });
    if (Array.isArray(row.children)) {
      updatedRow.children = row.children.map(formatRow);
    }
    return updatedRow;
  };

  return tableData.map(formatRow);
};

const handleCellClick = (arg1, arg2) => {
  const fieldPath = arg1?.fieldPath || arg2?.field || arg2?.columnKey || arg1?.field;
  const rowData = arg1?.rowData || arg1;
  const isProjectNameCol = fieldPath === 'projectName' || fieldPath === 'project_name';
  const idValue = rowData?.projectId || rowData?.project_seq_id || rowData?.project_id;
  if (isProjectNameCol && idValue) {
    const targetUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(idValue))}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
};

const applyProjectNameLink = (data) => {
  if (!Array.isArray(data)) return data;

  const formatRow = (row) => {
    if (!row || typeof row !== 'object') return row;
    const existingConfig = row.rowConfiguration || {};
    const existingOnClick = Array.isArray(existingConfig.onClick) ? existingConfig.onClick : [];
    const fieldsToAdd = ['projectName', 'project_name'].filter(
      (field) => !existingOnClick.some((item) => item?.field === field),
    );

    const updatedRow = {
      ...row,
      ...(fieldsToAdd.length > 0
        ? {
          rowConfiguration: {
            ...existingConfig,
            onClick: [
              ...existingOnClick,
              ...fieldsToAdd.map((field) => ({ field })),
            ],
          },
        }
        : {}),
    };

    if (Array.isArray(row.children)) {
      updatedRow.children = row.children.map(formatRow);
    }
    return updatedRow;
  };

  return data.map(formatRow);
};

const handleFilteredApiResponse = (response, tableConfigurationCacheRef) => {
  if (!response) return;

  let rawTableConfiguration = [];
  if (Array.isArray(response.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.tableConfiguration;
  } else if (Array.isArray(response.data?.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.data.tableConfiguration;
  }

  if (rawTableConfiguration.length > 0) {
    rawTableConfiguration = rawTableConfiguration.map((col) => {
      if (col.field === 'projectName') return { ...col, pinned: true, defaultPinned: true };
      if (col.field === 'projectId') return { ...col, pinned: false, defaultPinned: false };
      return col;
    });
    if (tableConfigurationCacheRef) tableConfigurationCacheRef.current = rawTableConfiguration;
  } else if (tableConfigurationCacheRef?.current?.length) {
    rawTableConfiguration = tableConfigurationCacheRef.current;
  }

  const rawTableData = response.data?.tableData || response.data?.data?.tableData || [];
  const formattedTableData = applyNumericFormatting(rawTableConfiguration, rawTableData);
  const dataWithProjectLink = applyProjectNameLink(formattedTableData);

  response.data = {
    ...response.data,
    tableConfiguration: rawTableConfiguration,
    tableData: dataWithProjectLink,
    data: {
      ...(response.data?.data || {}),
      tableConfiguration: rawTableConfiguration,
      tableData: dataWithProjectLink,
    },
  };

  response.meta = {
    ...(response.meta || {}),
    total: response.meta?.total ?? response.data?.tableData?.length ?? 0,
  };
};

const applyNumericAlignment = () => {
  const container = document.querySelector('.accrued-revenue-report-table-utility');
  if (!container) return;

  const tables = container.querySelectorAll('table, .p-datatable-table');
  if (!tables.length) return;

  tables.forEach((table) => {
    const headerCells = table.querySelectorAll('.p-datatable-thead tr th, thead tr th');
    headerCells.forEach((hdr) => {
      const headerText = (hdr.textContent || '').toLowerCase().trim();
      const isNumeric = NUMERIC_COLUMN_HEADER_PATTERNS.some((pattern) => pattern.test(headerText));

      const colIndex = Array.from(hdr.parentElement.children).indexOf(hdr) + 1;
      const bodyCells = table.querySelectorAll(
        `.p-datatable-tbody tr td:nth-child(${colIndex}), tbody tr td:nth-child(${colIndex})`,
      );

      if (isNumeric) {
        hdr.classList.add('numeric-aligned');
        hdr.style.textAlign = 'right';

        const headerContent = hdr.querySelector('.p-column-header-content');
        if (headerContent) {
          headerContent.style.justifyContent = 'flex-end';
          headerContent.style.textAlign = 'right';
        }

        const headerCol = hdr.querySelector('.header-column');
        if (headerCol) {
          headerCol.style.justifyContent = 'flex-end';
          headerCol.style.textAlign = 'right';
          const span = headerCol.querySelector('span');
          if (span) {
            span.style.textAlign = 'right';
            span.style.width = '100%';
          }
        }

        bodyCells.forEach((cell) => {
          cell.classList.add('numeric-aligned');
          cell.style.textAlign = 'right';
          const div = cell.querySelector('div');
          if (div) {
            div.style.textAlign = 'right';
          }
        });
      } else {
        hdr.classList.remove('numeric-aligned');
        hdr.style.textAlign = '';

        const headerContent = hdr.querySelector('.p-column-header-content');
        if (headerContent) {
          headerContent.style.justifyContent = '';
          headerContent.style.textAlign = '';
        }

        const headerCol = hdr.querySelector('.header-column');
        if (headerCol) {
          headerCol.style.justifyContent = '';
          headerCol.style.textAlign = '';
          const span = headerCol.querySelector('span');
          if (span) {
            span.style.textAlign = '';
            span.style.width = '';
          }
        }

        bodyCells.forEach((cell) => {
          cell.classList.remove('numeric-aligned');
          cell.style.textAlign = '';
          const div = cell.querySelector('div');
          if (div) {
            div.style.textAlign = '';
          }
        });
      }
    });
  });
};

const AccruedRevenueMoreActionsMenu = () => {
  const navigate = useNavigate();
  const moreMenuRef = useRef(null);

  const menuItems = useMemo(() => [
    {
      label: 'Revenue Recognition Report',
      command: () => navigate('/mis/revenue-recognition'),
    },
  ], [navigate]);

  return (
    <>
      <Menu
        ref={moreMenuRef}
        model={menuItems}
        popup
        appendTo={document.body}
        className="accrued-revenue-more-menu-popup"
      />
      <Button
        type="button"
        icon="pi pi-ellipsis-v"
        className="p-button-text accrued-revenue-more-btn"
        onClick={(event) => moreMenuRef.current?.toggle(event)}
        aria-label="More Actions"
        tooltip="More options"
        tooltipOptions={{ position: 'top' }}
      />
    </>
  );
};

const ToolBarHeading = () => (
  <div className="accrued-revenue-report-toolbar-heading">
    <span className="accrued-revenue-report-toolbar-heading-title">
      Accrued Revenue Report for FP projects
    </span>
    <div className="accrued-revenue-report-toolbar-heading-right">
      <span className="accrued-revenue-report-toolbar-heading-note">
        <strong>Note:</strong> The amounts in the table are in INR format
      </span>
      <span className="accrued-revenue-report-toolbar-heading-separator" aria-hidden="true" />
    </div>
  </div>
);

const useAccruedRevenueTableEffects = ({ setTableScrollHeight }) => {
  useEffect(() => {
    document.body.classList.add('accrued-revenue-report-page');
    return () => {
      document.body.classList.remove('accrued-revenue-report-page');
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setTableScrollHeight(`${Math.floor(window.innerHeight * 0.7)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setTableScrollHeight]);

  useEffect(() => {
    const observer = new MutationObserver(applyNumericAlignment);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
};

const AccruedRevenueReport = () => {
  const { userProfile } = useAuth();
  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const tableConfigurationCacheRef = useRef([]);

  const handleFilteredApiResponseWithState = useCallback((response) => {
    handleFilteredApiResponse(response, tableConfigurationCacheRef);
    setHasAppliedFilters(true);
  }, []);

  useAccruedRevenueTableEffects({ setTableScrollHeight });

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,

      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },

      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: true },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('Accrued Revenue Report'),
          sheetName: () => getExportFileNameWithDate('Accrued Revenue Report'),
        },
      },

      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse: handleFilteredApiResponseWithState,
        setTableStateHandler: (getTableState) => {
          const tableState = getTableState?.();
          const configFilters = getAppliedFiltersFromConfiguration(
            tableState?.tableConfiguration || [],
          );
          const stateFilters = Array.isArray(tableState?.filters)
            ? tableState.filters.filter(hasFilterValue)
            : [];
          const appliedFilters = stateFilters.length > 0 ? stateFilters : configFilters;
          if (appliedFilters.length > 0) {
            setHasAppliedFilters(true);
          }
        },
      },

      headers: {
        token: userProfile.access_token,
      },

      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,

      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: <ToolBarHeading />,
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
        additionalElements: [<AccruedRevenueMoreActionsMenu key="accrued-revenue-more-actions" />],
      },
    },

    tableSettings: {
      size: 'small',
      loaderSkeleton: { rows: 8, bodyCells: 2, columns: 7 },
      showExpandCollapse: false,
      tableScrollHeight,
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [tableScrollHeight, handleFilteredApiResponseWithState, userProfile]);

  const isInitialState = !hasAppliedFilters;

  return (
    <div
      className={`accrued-revenue-report-outer-layer${isInitialState ? ' accrued-revenue-report--initial' : ''}`}
    >
      <div className="accrued-revenue-report">
        <div className="accrued-revenue-report-table-utility">
          <TableUtility configuration={configuration} showFilterOption />
        </div>
        {isInitialState && <MilestoneInitial />}
      </div>
    </div>
  );
};

export default AccruedRevenueReport;
